import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ShoppingCart, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const APP_ICON = "https://static.prod-images.emergentagent.com/jobs/3c404c7b-5045-4d45-8ddd-9111c2fbc8c7/images/1edd49db85dba9cc04f46bd52929381e881d2b7dac026dd5c68dd6bec34e756c.png";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password, rememberMe);
      toast.success('Logget ind!');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 auth-bg relative">
        <div className="absolute inset-0 bg-[#2F3E3B]/50 flex items-center justify-center">
          <div className="text-center text-white px-8">
            <ShoppingCart className="w-16 h-16 mx-auto mb-6 opacity-90" />
            <h1 className="text-5xl mb-4 font-medium tracking-tight">Indkøbsliste</h1>
            <p className="text-lg opacity-80 font-sans">Din smarte indkoebsassistent</p>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-[#F9F8F6]">
        <Card className="w-full max-w-md border-[#EAE6DF] shadow-sm">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex justify-center mb-3">
              <img src={APP_ICON} className="w-14 h-14 rounded-xl" alt="Logo" />
            </div>
            <CardTitle className="text-3xl text-[#2F3E3B] tracking-tight">Log ind</CardTitle>
            <CardDescription className="text-[#5C706A] font-sans">Indtast dine oplysninger for at fortsætte</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm font-sans" data-testid="login-error">{error}</div>}
              <div className="space-y-1.5">
                <Label className="font-sans text-[#2F3E3B]">Email</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="din@email.dk"
                  required className="border-[#EAE6DF] focus:ring-[#7F9C82]/30 focus:border-[#7F9C82]" data-testid="login-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="font-sans text-[#2F3E3B]">Adgangskode</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Din adgangskode" required className="border-[#EAE6DF] focus:ring-[#7F9C82]/30 focus:border-[#7F9C82] pr-10"
                    data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C706A] hover:text-[#2F3E3B] transition-colors">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox id="remember" checked={rememberMe} onCheckedChange={setRememberMe} data-testid="login-remember-me" />
                  <Label htmlFor="remember" className="text-sm font-sans text-[#5C706A] cursor-pointer">Husk mig</Label>
                </div>
                <Link to="/forgot-password" className="text-sm font-sans text-[#7F9C82] hover:text-[#658368] hover:underline transition-colors"
                  data-testid="forgot-password-link">Glemt adgangskode?</Link>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#7F9C82] hover:bg-[#658368] text-white font-sans font-medium transition-colors"
                data-testid="login-submit-button">
                {loading ? 'Logger ind...' : 'Log ind'}
              </Button>
              <p className="text-center text-sm font-sans text-[#5C706A]">
                Har du ikke en konto?{' '}
                <Link to="/register" className="text-[#7F9C82] hover:text-[#658368] hover:underline font-medium transition-colors"
                  data-testid="register-link">Opret profil</Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
